import React, { FC, ReactNode, useRef, useState } from 'react'
import { Box, Button, ButtonGroup, ButtonProps, Fade, IconButton, InputBaseProps, SxProps, Theme, Typography } from '@mui/material'
import { FormControlLabel, FormGroup, Radio, RadioGroup, TextField, colors, styled } from '@mui/material'
import { ToggleButton, Tooltip, ToggleButtonGroup, Checkbox, IconButtonProps, InputAdornment, InputBase } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ShuffleIcon from '@mui/icons-material/Shuffle'
import ClearAllIcon from '@mui/icons-material/ClearAll'
import FilterListIcon from '@mui/icons-material/FilterList'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { mapDataViewContext } from './context'
import { IFilterLogic, IFilterDateLogic, IFilterDateRangeLogic, IFilterSelectDef, IValidationResult, IFilterDateDef, IFilterBasicDef } from './types'
import RichTooltip, { HelpOutlinePulseIcon } from '../components/rich-tooltip'
import { createFilterStore } from './filter-store'

export const mapSortConfigs: Record<'asc' | 'desc', { title: string; icon: ReactNode }> = {
  asc: { title: 'Ascending', icon: <ArrowUpwardIcon fontSize='small' /> },
  desc: { title: 'Descending', icon: <ArrowDownwardIcon fontSize='small' /> }
}

//#region Toggle Components
export interface IFilterLogicToggleProps {
  value?: IFilterLogic
  onChange?: (event: React.MouseEvent<HTMLElement>, value: IFilterLogic) => void
}

export const FilterLogicToggle: FC<IFilterLogicToggleProps> = (props) => {
  const handleChange = (event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue && props.onChange) {
      props.onChange(event, newValue as IFilterLogic)
    }
  }

  return (
    <ToggleButtonGroupCustom size='small' value={props.value || 'and'} exclusive onChange={handleChange}>
      <ToggleButton size='small' value='and' aria-label='and logic'>
        AND
      </ToggleButton>
      <ToggleButton size='small' value='or' aria-label='or logic'>
        OR
      </ToggleButton>
    </ToggleButtonGroupCustom>
  )
}

export interface IDateLogicToggleProps {
  value?: IFilterDateLogic
  onChange?: (event: React.MouseEvent<HTMLElement>, value: IFilterDateLogic) => void
}

export const DateLogicToggle: FC<IDateLogicToggleProps> = (props) => {
  const handleChange = (event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue && props.onChange) {
      props.onChange(event, newValue as IFilterDateLogic)
    }
  }

  return (
    <ToggleButtonGroupCustom size='small' value={props.value || 'before'} exclusive onChange={handleChange}>
      <ToggleButton size='small' value='before' aria-label='before date'>
        before
      </ToggleButton>
      <ToggleButton size='small' value='after' aria-label='after date'>
        after
      </ToggleButton>
    </ToggleButtonGroupCustom>
  )
}

export interface IDateRangeLogicToggleProps {
  value?: IFilterDateRangeLogic
  onChange?: (event: React.MouseEvent<HTMLElement>, value: IFilterDateRangeLogic) => void
}

export const DateRangeLogicToggle: FC<IDateRangeLogicToggleProps> = (props) => {
  const handleChange = (event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue && props.onChange) {
      props.onChange(event, newValue as IFilterDateRangeLogic)
    }
  }

  return (
    <ToggleButtonGroupCustom size='small' value={props.value || 'between'} exclusive onChange={handleChange}>
      <ToggleButton size='small' value='between' aria-label='between dates'>
        between
      </ToggleButton>
      <ToggleButton size='small' value='not-between' aria-label='not between dates'>
        not between
      </ToggleButton>
    </ToggleButtonGroupCustom>
  )
}

const ToggleButtonGroupCustom = styled(ToggleButtonGroup)({
  '& .MuiToggleButton-root': {
    color: '#fff',
    borderColor: 'rgba(255, 255, 255, 0.3)',
    fontSize: '0.65rem',
    fontWeight: 600,
    lineHeight: 1,
    minWidth: '40px',
    padding: '6px 8px',
    '&.Mui-selected': {
      backgroundColor: '#1976d2',
      color: '#fff',
      '&:hover': {
        backgroundColor: '#1565c0'
      }
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)'
    }
  }
})
//#endregion

//#region Notes
export interface IFilterNotesProps {
  title?: string
  items?: string[]
  disabledSize?: boolean
  renderContent?: () => ReactNode
  sx?: SxProps<Theme>
}

const HelpTooltipContent: FC<{ items: string[] }> = (props) => (
  <WrapList>
    {props.items.map((item, index) => (
      <Typography key={index} component='li' variant='body2'>
        {item}
      </Typography>
    ))}
  </WrapList>
)

export const FilterNotes: FC<IFilterNotesProps> = (props) => (
  <WrapIcon className={props.disabledSize === true ? 'disabled-size' : ''} sx={props.sx}>
    <RichTooltip
      panel={{
        title: props.title ?? 'The search includes',
        content: props.renderContent ? props.renderContent() : <HelpTooltipContent items={props.items ?? []} />
      }}
    >
      <HelpOutlinePulseIcon fontSize='small' />
    </RichTooltip>
  </WrapIcon>
)
//#endregion

//#region Input
export const FilterInput = React.forwardRef<HTMLInputElement, InputBaseProps>((props, ref) => {
  return <InputBase fullWidth autoComplete='off' size='small' {...props} inputRef={ref} />
})

interface IFilterInputAdornmentProps {
  notes?: IFilterNotesProps
  keyword?: string
  onClear: IconButtonProps['onClick']
  onEnterSearch: ButtonProps['onClick']
}

export const FilterInputAdornment: FC<IFilterInputAdornmentProps> = (props) => (
  <InputAdornment position='end'>
    {props.keyword && (
      <>
        <HelpButton size='small' color='primary' onClick={props.onEnterSearch} sx={{ display: { xs: 'none', md: 'flex' } }}>
          <Typography variant='caption'>
            Type and hit <b>Enter ⏎</b> to filter.
          </Typography>
        </HelpButton>
        <Tooltip title='Press Enter to filter' placement='bottom' arrow>
          <IconButton size='small' color='primary' sx={{ display: { xs: 'flex', md: 'none' } }} onClick={props.onEnterSearch}>
            <KeyboardReturnIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </>
    )}
    {props.keyword && (
      <Tooltip title='Press Esc to clear' placement='bottom' arrow>
        <IconButton size='small' sx={{ marginLeft: '8px', color: 'text.error', '&:hover': { color: 'error.main' } }} onClick={props.onClear}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Tooltip>
    )}
    {props.notes && <FilterNotes {...props.notes} />}
  </InputAdornment>
)

const HelpButton = styled(Button)({
  padding: '4px 6px',
  textTransform: 'none',
  lineHeight: 1,
  '.MuiTypography-root': {
    fontWeight: 600,
    b: {
      fontWeight: 700,
      backgroundColor: colors.blue[100],
      borderRadius: '4px',
      padding: '2px 4px'
    }
  }
})
//#endregion

//#region Button
export interface IButtonClearProps {
  visibled?: boolean
  onClick: IconButtonProps['onClick']
}

export const ButtonClear: FC<IButtonClearProps> = (props) => (
  <WrapIcon>
    <Tooltip title='Remove filter'>
      <Fade in={props.visibled} unmountOnExit>
        <IconButton size='small' onClick={props.onClick}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Fade>
    </Tooltip>
  </WrapIcon>
)

export const ClearAllButton = React.forwardRef<HTMLButtonElement, ButtonProps>(({ children, ...props }, ref) => (
  <ClearButtonStyled size='small' color='error' startIcon={<ClearAllIcon />} ref={ref} {...props}>
    {children || 'Clear all'}
  </ClearButtonStyled>
))

const ClearButtonStyled = styled(Button)({ textTransform: 'none', fontWeight: 700 })

export const FilterButton = React.forwardRef<HTMLButtonElement, IconButtonProps>((props, ref) => (
  <IconButton ref={ref} size='small' sx={{ flex: '0 0 auto' }} {...props}>
    <FilterListIcon />
  </IconButton>
))

export const SortIconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>((props, ref) => (
  <IconButton ref={ref} size='small' sx={{ flex: '0 0 auto' }} {...props}>
    <ShuffleIcon />
  </IconButton>
))

export const SortButton: FC<ButtonProps> = (props) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const debounceDelay = 500
  return (
    <>
      {mapDataViewContext((context) => {
        const { filterState, filterBarConfigs: configs } = context
        const config: Partial<{ title: string; icon: ReactNode }> = filterState.sort ? mapSortConfigs[filterState.sort?.direction] : {}
        const fieldConfig = configs.fields?.[filterState.sort?.field as any]
        const text = fieldConfig?.label || filterState.sort?.field?.toString() || configs.sortButton?.text
        // Debounce logic to prevent rapid/continuous calls
        const handleSortButtonClick = () => {
          if (!filterState.sort?.field) return
          const currentDirection = filterState.sort?.direction
          const newDirection: 'asc' | 'desc' = currentDirection ? (currentDirection === 'asc' ? 'desc' : 'asc') : 'asc'
          const newSort = { field: filterState.sort.field, direction: newDirection }
          const store = createFilterStore(filterState).changeSort(newSort, { area: 'sortButton' })
          context.onFilterStateChange(store.build())
        }

        const handlePopperPanelChange = (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation()
          timeoutRef.current && clearTimeout(timeoutRef.current)
          timeoutRef.current = setTimeout(() => handleSortButtonClick(), debounceDelay)
        }

        if (text) {
          return (
            <ButtonGroup aria-label='Button Sort' size='small'>
              <SortButtonStyled
                size='small'
                color='inherit'
                startIcon={config.icon || <ShuffleIcon fontSize='small' />}
                onClick={handlePopperPanelChange}
                {...configs.sortButton?.wrapProps}
                {...props}
              >
                {text}
              </SortButtonStyled>
              <SortButtonStyled
                size='small'
                color='inherit'
                sx={{ p: 0 }}
                onClick={(e) => context.onPopperPanelChange(e.currentTarget, 'sortButton')}
              >
                <KeyboardArrowDownIcon fontSize='small' />
              </SortButtonStyled>
            </ButtonGroup>
          )
        }

        return (
          <SortButtonStyled
            size='small'
            color='inherit'
            startIcon={config.icon || <ShuffleIcon fontSize='small' />}
            endIcon={configs.sortButton?.icon ?? <KeyboardArrowDownIcon fontSize='small' />}
            onClick={(e) => context.onPopperPanelChange(e.currentTarget, 'sortButton')}
            {...configs.sortButton?.wrapProps}
            {...props}
          >
            {text || 'Sort'}
          </SortButtonStyled>
        )
      })}
    </>
  )
}

const SortButtonStyled = styled(Button)(({ theme }) => ({
  height: 'var(--filter-bar-height, 40px)',
  padding: '0 16px',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50],
  border: '1px solid var(--color-divider, rgba(145 158 171 / 20%))',
  textTransform: 'none',
  fontWeight: 600,
  ':hover': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : colors.grey[100]
  }
}))
//#endregion

//#region Panel Not Found
export const PanelNotFound: FC<{ area?: string }> = ({ area }) => (
  <Box sx={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', borderRadius: '4px' }}>
    <Typography variant='body2' sx={{ padding: '8px' }}>
      No panel available for area: {area}
    </Typography>
  </Box>
)
//#endregion

//#region Form
interface IFormContentProps {
  label: string
  notes?: any
  children?: React.ReactNode
  afterTopBar?: React.ReactNode
}

const FormContent: FC<IFormContentProps> = ({ label, notes, children, afterTopBar }) => (
  <FormContentWrapper>
    <Box className='top-bar'>
      <Typography variant='subtitle2'>{label}</Typography>
      {afterTopBar && <Box className='after-top-bar'>{afterTopBar}</Box>}
      {notes && <FilterNotes title={`The ${label} includes`} disabledSize {...notes} />}
    </Box>
    <Box className='content'>{children}</Box>
  </FormContentWrapper>
)

const FormContentWrapper = styled(Box)({
  minWidth: '200px',
  borderRadius: '4px',
  overflow: 'hidden',
  '.top-bar': {
    display: 'flex',
    alignItems: 'center',
    background: colors.grey[900],
    padding: '8px',
    gap: '8px',
    '.MuiTypography-root': {
      color: colors.common.white,
      flex: 1
    }
  },
  '.after-top-bar': {
    marginLeft: '8px'
  },
  '.content': {
    padding: '8px'
  }
})
//#endregion

//#region Menu - String Type
interface IMenuWithTypeBaseProps {
  name: string
  validationResult?: IValidationResult
}

interface IMenuWithTypeStringProps extends IMenuWithTypeBaseProps {
  fieldConfig?: IFilterBasicDef
}

export const MenuWithTypeString: FC<IMenuWithTypeStringProps> = (props) => {
  const label = props.fieldConfig?.label ?? props.name
  return (
    <FormContent label={label} notes={props.fieldConfig?.notes}>
      <TextField
        autoFocus
        name={props.name}
        size='small'
        fullWidth
        error={!!props.validationResult?.hasError}
        helperText={props.validationResult?.message}
        sx={{ '.MuiInputBase-root': { minHeight: '42px' } }}
      />
    </FormContent>
  )
}
//#endregion

//#region Menu - Select Type
export interface IMenuWithTypeSelectProps extends IMenuWithTypeBaseProps {
  fieldConfig?: IFilterSelectDef
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export const MenuWithTypeSelect: FC<IMenuWithTypeSelectProps> = (props) => {
  const label = props.fieldConfig?.label ?? props.name
  return (
    <FormContent label={label} notes={props.fieldConfig?.notes}>
      <CustomRadioGroup name={props.name} className={props.validationResult?.hasError ? 'error' : ''} onChange={props.onChange}>
        {props.fieldConfig?.options.map((x, i) => (
          <FormControlLabel key={x.value.toString() + i} value={x.value} control={<Radio />} label={x.label ?? x.value} />
        ))}
      </CustomRadioGroup>
    </FormContent>
  )
}

const CustomRadioGroup = styled(RadioGroup)({
  marginLeft: '-8px',
  marginRight: '-8px',
  '&.error .MuiRadio-root': {
    color: '#d32f2f'
  },
  '.MuiFormControlLabel-root': {
    margin: 0
  },
  '.MuiFormControlLabel-root:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.04)'
  }
})
//#endregion

//#region Menu - Select Multi Type
export interface IMenuWithTypeSelectMultiProps extends IMenuWithTypeBaseProps {
  fieldConfig?: IFilterSelectDef
  onChange?: (event: React.SyntheticEvent, checked: boolean) => void
}

export const MenuWithTypeSelectMulti: FC<IMenuWithTypeSelectMultiProps> = (props) => {
  const [filterLogic, setFilterLogic] = useState<IFilterLogic>('and')
  const label = props.fieldConfig?.label ?? props.name
  return (
    <FormContent
      label={label}
      notes={props.fieldConfig?.notes}
      afterTopBar={<FilterLogicToggle value={filterLogic} onChange={(_, value) => setFilterLogic(value)} />}
    >
      {/* Hidden input for logic value */}
      <input type='hidden' name='filterLogic' value={filterLogic} />
      <CustomFormGroup className={props.validationResult?.hasError ? 'error' : ''}>
        {props.fieldConfig?.options.map((x, i) => (
          <FormControlLabel
            key={x.value.toString() + i}
            value={x.value}
            control={<Checkbox name={props.name} />}
            label={x.label ?? x.value}
            onChange={props.onChange}
          />
        ))}
      </CustomFormGroup>
    </FormContent>
  )
}

const CustomFormGroup = styled(FormGroup)({
  marginLeft: '-8px',
  marginRight: '-8px',
  '&.error .MuiCheckbox-root': {
    color: '#d32f2f'
  },
  '.MuiFormControlLabel-root': {
    margin: 0
  },
  '.MuiFormControlLabel-root:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.04)'
  }
})
//#endregion

function convertDateToCustomFormat(dateString: string): string {
  if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return ''
  return `${dateString}T00:00:00.0000000`
}
//#region Menu - Date Type
export interface IMenuWithTypeDateProps extends IMenuWithTypeBaseProps {
  fieldConfig?: IFilterDateDef
}

export const MenuWithTypeDate: FC<IMenuWithTypeDateProps> = (props) => {
  const [dateLogic, setDateLogic] = useState<IFilterDateLogic>('before')
  const [value, setValue] = useState<string>('')
  const label = props.fieldConfig?.label ?? props.name
  return (
    <FormContent
      label={label}
      notes={props.fieldConfig?.notes}
      afterTopBar={<DateLogicToggle value={dateLogic} onChange={(_, value) => setDateLogic(value)} />}
    >
      <Box className='content'>
        {/* Hidden input for logic value */}
        <input type='hidden' name='dateLogic' value={dateLogic} />
        <input hidden type='text' name={props.name} readOnly value={convertDateToCustomFormat(value)} />
        <TextField
          autoFocus
          fullWidth
          type='date'
          size='small'
          value={value}
          onChange={(event) => setValue(event.target.value)}
          error={!!props.validationResult?.hasError}
          helperText={props.validationResult?.message}
          inputProps={{ min: props.fieldConfig?.minDate, max: props.fieldConfig?.maxDate }}
          sx={{ '.MuiInputBase-root': { minHeight: '42px' } }}
        />
      </Box>
    </FormContent>
  )
}
//#endregion

//#region Menu - Date Range Type
export interface IMenuWithTypeDateRangeProps extends IMenuWithTypeBaseProps {
  fieldConfig?: IFilterDateDef
}

export const MenuWithTypeDateRange: FC<IMenuWithTypeDateRangeProps> = (props) => {
  const [dateRangeLogic, setDateRangeLogic] = useState<IFilterDateRangeLogic>('between')
  const label = props.fieldConfig?.label ?? props.name
  return (
    <FormContent
      label={label}
      notes={props.fieldConfig?.notes}
      afterTopBar={<DateRangeLogicToggle value={dateRangeLogic} onChange={(_, value) => setDateRangeLogic(value)} />}
    >
      {/* Hidden input for logic value */}
      <input type='hidden' name='dateRangeLogic' value={dateRangeLogic} />
      <DateRangeContainer>
        <TextField
          autoFocus
          fullWidth
          name={`${props.name}_start`}
          type='date'
          size='small'
          label='From'
          error={!!props.validationResult?.hasError}
          sx={{ marginBottom: 1 }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: props.fieldConfig?.minDate, max: props.fieldConfig?.maxDate }}
        />
        <TextField
          fullWidth
          name={`${props.name}_end`}
          type='date'
          size='small'
          label='To'
          error={!!props.validationResult?.hasError}
          helperText={props.validationResult?.message}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: props.fieldConfig?.minDate, max: props.fieldConfig?.maxDate }}
        />
      </DateRangeContainer>
    </FormContent>
  )
}

const DateRangeContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
})
//#endregion

//#region Styled
const WrapIcon = styled(Box)({
  display: 'inline-flex',
  flex: '0 0 auto',
  justifyContent: 'center',
  alignItems: 'center',
  '&:not(.disabled-size)': {
    width: '40px',
    height: '40px',
    '& > div': { display: 'flex' }
  }
})

const WrapList = styled('ul')({
  paddingLeft: '1.7rem',
  marginBottom: 0,
  li: {
    position: 'relative',
    textAlign: 'justify',
    '&::before': {
      content: '"►"',
      display: 'block',
      position: 'absolute',
      top: '50%',
      right: 'calc(100% + 6px)',
      transform: 'translateY(-50%)',
      fontSize: '0.9em'
    }
  }
})
//#endregion
